const ACCESSORY_WORDS = [
  'spring', 'springs', 'hdmi', 'socket', 'port', 'connector', 'thumbstick',
  'thumbsticks', 'joystick', 'joysticks', 'grip', 'cap', 'cover', 'replacement',
  'shell', 'cable', 'kabel', 'cord', 'button', 'buttons', 'trigger', 'triggers',
  'sticker', 'decal', 'skin', 'film', 'protector', 'adapter', 'dock', 'stand',
  'mount', 'bracket', 'screw', 'screws', 'tool', 'tools', 'repair', 'parts',
  'part', 'case', 'pouch', 'bag', 'charger', 'charging', 'battery', 'strap',
  'band', 'holder', 'clip', 'pad', 'dust', 'filter', 'filters', 'mesh', 'ring',
  'rings', 'assist', 'precision', 'vertical', 'support', 'housse', 'sleeve',
  'faceplate', 'plate', 'housing', 'bumper', 'paddle', 'extender', 'feet',
  'foot', 'fan', 'cooling', 'heatsink', 'silicone', 'rubber', 'analog', 'stick',
  'sticks', 'drift', 'mod', 'mods', 'compatible', 'accessory', 'accessories',
  'accessoire', 'accessoires', '1pc', '1pcs', 'pcs', 'piece', 'pieces', 'pack',
  'set', 'kit', 'spare', 'thumb', 'controller', 'skin', 'wrap', 'label',
];

const MAIN_PRODUCT_WORDS = [
  'console', 'bundle', 'system', 'playstation', 'xbox', 'nintendo',
];

const PRICE_FLOOR_RULES = [
  { pattern: 'ps5 pro', minPriceUsd: 350 },
  { pattern: 'ps5', minPriceUsd: 150 },
  { pattern: 'xbox series x', minPriceUsd: 250 },
  { pattern: 'xbox series s', minPriceUsd: 150 },
  { pattern: 'xbox', minPriceUsd: 80 },
  { pattern: 'nintendo switch', minPriceUsd: 150 },
  { pattern: 'iphone', minPriceUsd: 80 },
  { pattern: 'samsung galaxy', minPriceUsd: 80 },
];

const ACCESSORY_TITLE_PATTERNS = [
  /\b(for|pour|für|per|do|fur)\s+(ps\d|xbox|switch|playstation|iphone|galaxy)\b/i,
  /\b(for|pour)\s+console\b/i,
  /\baim\s+assist\b/i,
  /\bdust\s*(filter|mesh|cover|plug|stopper)?\b/i,
  /\bvertical\s+(support|stand)\b/i,
  /\bprecision\s+ring/i,
  /\b(usb[\s-]?c|hdmi|charging)\s+(cable|kabel|cord)\b/i,
  /\b(console\s+)?(dust|fan)\s+(filter|mesh|cover)\b/i,
  /\bhousse\b/i,
  /\bkabel\b/i,
  /\b\d+\s*(pcs|pc|pack|set)\b/i,
  /\breplacement\s+(part|shell|cover|plate)\b/i,
];

const ACCESSORY_HIDE_THRESHOLD = 2;

function stemWord(word) {
  if (!word) return '';
  return word.replace(/(?:ings?|ers?|ing|ed|s)$/, '');
}

function tokenize(text) {
  return String(text || '')
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

function titleHasWord(titleWords, word) {
  const stem = stemWord(word);
  return titleWords.some(
    (titleWord) => titleWord === word || stemWord(titleWord) === stem
  );
}

function countAccessoryWords(titleWords) {
  let count = 0;
  ACCESSORY_WORDS.forEach((word) => {
    if (titleHasWord(titleWords, word)) {
      count += 1;
    }
  });
  return count;
}

function getPriceFloorUsd(queryWords, searchText) {
  const normalizedQuery = String(searchText || '').toLowerCase().trim();
  let bestFloor = null;

  PRICE_FLOOR_RULES.forEach((rule) => {
    const patternWords = rule.pattern.toLowerCase().split(' ').filter(Boolean);
    const patternMatches = patternWords.every(
      (patternWord) => queryWords.includes(patternWord)
        || normalizedQuery.includes(patternWord)
    );
    if (patternMatches) {
      if (bestFloor === null || rule.minPriceUsd > bestFloor) {
        bestFloor = rule.minPriceUsd;
      }
    }
  });

  return bestFloor;
}

function hasAccessoryTitlePattern(title) {
  const normalized = String(title || '').toLowerCase();
  return ACCESSORY_TITLE_PATTERNS.some((pattern) => pattern.test(normalized));
}

function hasMainProductKeyword(titleWords) {
  return MAIN_PRODUCT_WORDS.some((word) => titleHasWord(titleWords, word));
}

function getProductPriceUsd(product) {
  const store = product?.otherStores?.[0];
  const price = store?.price;
  if (!price) return 0;

  const usdPrice = price?.extra?.USD?.calcTotalPrice;
  if (usdPrice !== undefined && usdPrice !== null && usdPrice !== '') {
    return parseFloat(usdPrice) || 0;
  }

  if (price.calcCurrency === 'USD') {
    return parseFloat(price.calcTotalPrice) || 0;
  }

  return parseFloat(price.calcTotalPrice) || 0;
}

export function detectAccessory(title, searchQuery, priceUsd) {
  const queryWords = tokenize(searchQuery);
  const titleWords = tokenize(title);
  const accessoryWordCount = countAccessoryWords(titleWords);

  let isLikelyAccessory = accessoryWordCount >= ACCESSORY_HIDE_THRESHOLD;

  if (
    !isLikelyAccessory
    && accessoryWordCount >= 1
    && queryWords.length > 0
  ) {
    const priceFloor = getPriceFloorUsd(queryWords, searchQuery);
    if (priceFloor !== null && priceUsd > 0 && priceUsd < priceFloor * 0.5) {
      isLikelyAccessory = true;
    }
  }

  if (!isLikelyAccessory && hasAccessoryTitlePattern(title)) {
    isLikelyAccessory = true;
  }

  if (
    !isLikelyAccessory
    && queryWords.length >= 2
    && priceUsd > 0
  ) {
    const matchedQueryWords = queryWords.filter((word) => titleHasWord(titleWords, word)).length;
    const queryMatchRatio = matchedQueryWords / queryWords.length;
    const priceFloor = getPriceFloorUsd(queryWords, searchQuery);

    if (
      queryMatchRatio >= 0.5
      && priceFloor !== null
      && priceUsd < priceFloor * 0.65
      && (!hasMainProductKeyword(titleWords) || accessoryWordCount >= 1)
    ) {
      isLikelyAccessory = true;
    }
  }

  return {
    accessoryWordCount,
    isLikelyAccessory,
  };
}

export function isLikelyAccessory(product, searchQuery = '') {
  const title = product?.otherStores?.[0]?.title || '';
  const priceUsd = getProductPriceUsd(product);
  const local = detectAccessory(title, searchQuery, priceUsd);

  if (product?.isLikelyAccessory === true) {
    return true;
  }

  return local.isLikelyAccessory;
}
